// What the header search remembers (GROWTH-7577): the last searches per member (localStorage, max 5) and the
// scope and city of this browser session (sessionStorage). Every read and write is guarded: private windows and
// blocked site data throw, and the search must work without them.

export const MAX_RECENT = 5;

const RECENT_KEY = "hw_header_search_recent";
const SESSION_KEY = "hw_header_search_session";

const readJson = (storage, key, fallback) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (storage, key, value) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Nothing to do: the search works without remembering.
  }
};

const recentKey = login => `${RECENT_KEY}:${login || "anonymous"}`;

/**
 * @param {string} login the member
 * @returns {string[]} newest first, at most MAX_RECENT
 */
export const getRecent = login => {
  const stored = readJson(window.localStorage, recentKey(login), []);
  return Array.isArray(stored) ? stored.filter(item => typeof item === "string" && item !== "").slice(0, MAX_RECENT) : [];
};

/**
 * Puts a query first, dropping an earlier copy that differs only in case.
 *
 * @param {string} login
 * @param {string} query
 * @returns {string[]} the new list
 */
export const pushRecent = (login, query) => {
  const text = String(query || "").trim();
  if (text === "") return getRecent(login);

  const next = [text, ...getRecent(login).filter(item => item.toLowerCase() !== text.toLowerCase())].slice(0, MAX_RECENT);
  writeJson(window.localStorage, recentKey(login), next);

  return next;
};

export const clearRecent = login => {
  try {
    window.localStorage.removeItem(recentKey(login));
  } catch {
    // Nothing to do.
  }
};

/**
 * @returns {{scope: ?string, location: ?{city: string}|"anywhere", cities: string[]}} this session's choices;
 *   location is undefined until the member picks one
 */
export const getSession = () => {
  const stored = readJson(window.sessionStorage, SESSION_KEY, {});
  const cities = Array.isArray(stored.cities) ? stored.cities.filter(city => typeof city === "string") : [];

  return { scope: typeof stored.scope === "string" ? stored.scope : null, location: stored.location, cities };
};

/**
 * @param {Object} changes any of scope, location ({city} | "anywhere"), city (added to the cities used)
 */
export const saveSession = ({ scope, location, city }) => {
  const current = getSession();
  const cities = city ? [city, ...current.cities.filter(item => item !== city)].slice(0, MAX_RECENT) : current.cities;

  writeJson(window.sessionStorage, SESSION_KEY, {
    scope: scope !== undefined ? scope : current.scope,
    location: location !== undefined ? location : current.location,
    cities
  });
};
